import { useFormatter, useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { search } from "@/actions/currencyConversion";

// Interface for the Mortgage Calculator Return Data
interface IMortgageCalculatorData {
  propertyPrice: number;
  minPropertyPrice: number;
  maxPropertyPrice: number;
  propertyPriceStep: number;
  setPropertyPrice: (value: number) => void;
  handleSliderChange: (value: [number, number]) => void;
  loanPeriod: number;
  minLoanPeriod: number;
  maxLoanPeriod: number;
  loanPeriodStepSize: number;
  setLoanPeriod: (value: number) => void;
  handleSliderChangeLoanPeriod: (value: [number, number]) => void;
  loanPeriodInMonths: number;
  downPayment: number;
  setDownPayment: (value: number) => void;
  minDownPayment: number;
  maxDownPayment: number;
  downPaymentStepSize: number;
  handleSliderChangeDownPayment: (value: [number, number]) => void;
  interestRate: number;
  minInterestRate: number;
  maxInterestRate: number;
  interestRateStepSize: number;
  setInterestRate: (value: number) => void;
  handleSliderChangeInterestRate: (value: [number, number]) => void;
  resetToDefault: () => void;
  monthlyPaymentAmount: number;
  downpaymentAmount: number;
  ldFees: number;
  registerFees: number;
  mortgageRegFees: number;
  evaluationFees: number;
  formatStringIntoNumber: (str: string | number) => number;
  setDefaultPropertyPrice: () => void;
  setDefaultDownPayment: () => void;
  setDefaultLoanPeriod: () => void;
  setDefaultInterestRate: () => void;
}

const useMortgageCalculator = ({
  constants,
  priceMin,
  priceMax,
  currency,
  locale,
}: {
  constants: any;
  priceMin: number;
  priceMax: number;
  currency: string;
  locale: string;
}): IMortgageCalculatorData => {
  // Using for format the value into Comma seperated
  const format = useFormatter();
  // Define default values
  const defaultPropertyPrice = priceMin || constants?.defaultValues?.propertyPrice || "500000"; // Default Price
  const defaultLoanPeriod = constants?.defaultValues?.loanPeriod || 20; // 20 years * 12 months
  const defaultDownPayment = constants?.defaultValues?.downPayment || 20; // 20%
  const defaultInterestRate = constants?.defaultValues?.interestRate || 2.49; // 2.49%

  // Define min and max values and Step Size
  const minPropertyPrice = 500000; // Replace with the actual minimum property price
  const maxPropertyPrice = 100000000; // Replace with the actual maximum property price
  const propertyPriceStep = constants?.limits?.propertyPrice?.stepSize || 1000; // Property Price Step Size
  const minLoanPeriod = constants?.limits?.loanPeriod?.min || 1; // minimum Loan Period
  const maxLoanPeriod = constants?.limits?.loanPeriod?.max || 25; // Maximum Loan Period
  const loanPeriodStepSize = constants?.limits?.loanPeriod?.stepSize || 1; // Loan Period Step Size
  const minDownPayment = constants?.limits?.downPayment?.min || 0; // Minimum Downloan Payment
  const maxDownPayment = constants?.limits?.downPayment?.max || 80; // Maximum Downloan Payment
  const downPaymentStepSize = constants?.limits?.downPayment?.stepSize || 1; // Down PaymentStep Size
  const minInterestRate = constants?.limits?.interestRate?.min || 1; // Minimum Interest Rate
  const maxInterestRate = constants?.limits?.interestRate?.max || 10; // Maximum Interest Rate
  const interestRateStepSize = constants?.limits?.interestRate?.stepSize || 0.25; // Interest rate Step Size

  // State variables
  const [propertyPrice, setPropertyPrice] = useState<number>(defaultPropertyPrice);
  const [loanPeriod, setLoanPeriod] = useState<number>(defaultLoanPeriod);
  const [downPayment, setDownPayment] = useState<number>(defaultDownPayment);
  const [interestRate, setInterestRate] = useState<number>(defaultInterestRate);
  const [loanPeriodInMonths, setLoanPeriodInMonths] = useState<number>(loanPeriod);

  // Fee State variables
  const [ldFees, setLdFees] = useState<number>(0);
  const [registerFees, setRegisterFees] = useState<number>(0);
  const [mortgageRegFees, setMortgageRegFees] = useState<number>(0);
  const [evaluationFees, setEvaluationFees] = useState<number>(0);

  const [conversionRate, setConversionRate] = useState<number>(1);

  //Fetch conversion Rate Based on Currency
  useEffect(() => {
    (async () => {
      if (locale && currency) {
        try {
          const config = {
            locale: locale,
            currency: currency,
          };
          const conversionRateResponse: any = await search(config);
          if (conversionRateResponse && conversionRateResponse?.conversionrate) {
            setConversionRate(conversionRateResponse?.conversionrate);
          }
        } catch (error) {
          console.error("Err", error);
        }
      }
    })();
  }, [locale, currency]);

  // Using this UseEffect when the Currency change and currency is not AED then converting values into respective format
  useEffect(() => {
    setLdFees((prev) => Math.ceil((prev * conversionRate) / 1000) * 1000);
    setRegisterFees((prev) => Math.ceil((prev * conversionRate) / 1000) * 1000);
    setMortgageRegFees((prev) => Math.ceil((prev * conversionRate) / 1000) * 1000);
    setEvaluationFees((prev) => Math.ceil((prev * conversionRate) / 1000) * 1000);
  }, [conversionRate]);

  // Function to validate and set property price
  const handlePropertyPriceChange = (value: number) => {
    setPropertyPrice(value);
  };

  const setDefaultPropertyPrice = () => {
    if (isNaN(propertyPrice) || propertyPrice < minPropertyPrice) {
      setPropertyPrice(minPropertyPrice);
    } else if (propertyPrice > maxPropertyPrice) {
      setPropertyPrice(maxPropertyPrice);
    }
  };

  // Function to validate and set loan period
  const handleLoanPeriodChange = (value: number) => {
    setLoanPeriod(value);
  };
  const setDefaultLoanPeriod = () => {
    if (isNaN(loanPeriod) || loanPeriod < minLoanPeriod) {
      setLoanPeriod(minLoanPeriod);
    } else if (loanPeriod > maxLoanPeriod) {
      setLoanPeriod(maxLoanPeriod);
    }
  };

  // Function to validate and set down payment
  const handleDownPaymentChange = (value: number) => {
    setDownPayment(value);
  };

  const setDefaultDownPayment = () => {
    if (isNaN(downPayment) || downPayment < minDownPayment) {
      setDownPayment(minDownPayment);
    } else if (downPayment > maxDownPayment) {
      setDownPayment(maxDownPayment);
    }
  };

  // Function to validate and set interest rate
  const handleInterestRateChange = (value: number) => {
    setInterestRate(value);
  };

  const setDefaultInterestRate = () => {
    if (isNaN(interestRate) || interestRate < minInterestRate) {
      setInterestRate(minInterestRate);
    } else if (interestRate > maxInterestRate) {
      setInterestRate(maxInterestRate);
    }
  };

  // Function to reset values to default
  const resetToDefault = () => {
    setPropertyPrice(defaultPropertyPrice);
    setLoanPeriod(defaultLoanPeriod);
    setDownPayment(defaultDownPayment);
    setInterestRate(defaultInterestRate);
  };

  const handleSliderChange = (value: [number, number]) => {
    setPropertyPrice(value[1]); // Assuming slider returns an array with min and max values
  };

  const handleSliderChangeLoanPeriod = (value: [number, number]) => {
    setLoanPeriod(value[1]); // Assuming slider returns an array with min and max values
  };

  const handleSliderChangeDownPayment = (value: [number, number]) => {
    setDownPayment(value[1]); // Assuming slider returns an array with min and max values
  };

  const handleSliderChangeInterestRate = (value: [number, number]) => {
    setInterestRate(value[1]); // Assuming slider returns an array with min and max values
  };

  //Output Calculator Functions
  const getMonthlyPayment = (): number => {
    const youBorrow = propertyPrice - (propertyPrice * downPayment) / 100;
    const iRate = interestRate;
    const nYears = loanPeriod;
    const r = iRate / 100 / 12;
    const nMonths = nYears * 12;
    const monthlyPayment = youBorrow * ((r * Math.pow(1 + r, nMonths)) / (Math.pow(1 + r, nMonths) - 1));
    return parseInt(monthlyPayment.toFixed(0));
  };

  const getDownPaymentAmount = (): number => {
    var returnValue = (downPayment / 100) * propertyPrice;
    return parseInt(returnValue.toFixed(0));
  };

  //Outputs
  const [monthlyPaymentAmount, setMonthlyPaymentAmount] = useState<number>(getMonthlyPayment());
  const [downpaymentAmount, setDownpaymentAmount] = useState<number>(getDownPaymentAmount());

  useEffect(() => {
    setMonthlyPaymentAmount(getMonthlyPayment());
    setDownpaymentAmount(getDownPaymentAmount());
    calculateFees();
    setLoanPeriodInMonths(loanPeriod * 12);
  }, [propertyPrice, loanPeriod, downPayment, interestRate, currency, priceMin, priceMax]);

  useEffect(() => {
    setPropertyPrice(priceMin);
  }, [priceMin, priceMax]);

  const calculateLDFees = (): number => {
    //LDL Fees = 4% of property value + Admin Fee 580 AED
    var returnValue;
    returnValue = (4 / 100) * propertyPrice + 580;

    return returnValue;
  };

  const calculateRegisterFees = (): number => {
    // Register Fees = AED 4000 + 5% VAT for properties worth over AED 500000
    let returnValue = 0;
    if (propertyPrice > 500000) {
      returnValue = 4000 + (5 / 100) * 4000;
    } else {
      returnValue = 2000 + (5 / 100) * 2000;
    }

    return returnValue;
  };

  const calculateMortgageRegFees = (): number => {
    // Mortgage Registration Fees = 0.25% of Loan amount + AED 10 Admin Fee
    // Loan amount = Property price - downpayment
    const loanAmount = propertyPrice - downpaymentAmount;
    let returnValue = (0.25 / 100) * loanAmount + 10;

    return returnValue;
  };

  const calculateEvaluationFees = (): number => {
    // Evaluation fees = 3500 + 5% VAT (of 3500)
    let returnValue = 3500 + (5 / 100) * 3500;

    return returnValue;
  };

  const calculateFees = () => {
    setLdFees(calculateLDFees());
    setRegisterFees(calculateRegisterFees());
    setMortgageRegFees(calculateMortgageRegFees());
    setEvaluationFees(calculateEvaluationFees());
  };

  // Convert String Value into Number
  const formatStringIntoNumber = (data: string | number) => {
    if (typeof data === "string") return Number(data?.replace(/,/g, ""));
    return data;
  };

  return {
    // Property Price
    propertyPrice,
    minPropertyPrice,
    maxPropertyPrice,
    propertyPriceStep,
    setPropertyPrice: handlePropertyPriceChange,
    handleSliderChange,
    // Loan Period Props
    loanPeriod,
    minLoanPeriod,
    maxLoanPeriod,
    loanPeriodStepSize,
    setLoanPeriod: handleLoanPeriodChange,
    handleSliderChangeLoanPeriod,
    loanPeriodInMonths,
    // Down payment Props
    downPayment,
    setDownPayment: handleDownPaymentChange,
    minDownPayment,
    maxDownPayment,
    downPaymentStepSize,
    handleSliderChangeDownPayment,
    // Intereste rate Props
    interestRate,
    minInterestRate,
    maxInterestRate,
    interestRateStepSize,
    setInterestRate: handleInterestRateChange,
    handleSliderChangeInterestRate,
    resetToDefault,
    // Right Section - Monthly and Downpayment
    monthlyPaymentAmount,
    downpaymentAmount,
    // downpaymentAmount: formattedDownPayment,
    // Modal Land, Register, Mortgage and Evaluation Fees
    ldFees,
    registerFees,
    mortgageRegFees,
    evaluationFees,
    formatStringIntoNumber,
    setDefaultPropertyPrice,
    setDefaultDownPayment,
    setDefaultLoanPeriod,
    setDefaultInterestRate,
  };
};

export default useMortgageCalculator;
